import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { from, map, mergeMap, of, toArray, zip } from 'rxjs';
import { NumberUtils, Tr } from '@utils';
import { ASSESMENT_STATUS_COLOR_MAP } from '@models';
import { useLoggedinUser, useService } from '@hooks';
import { Assesment, AssesmentType, ParkArea, ParkAreaAssesmentStatus, Permission, Site, siteService } from '@services';
import { Grid, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { ParkAreaTable } from '@components/portal/parking-area';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LaunchIcon from '@mui/icons-material/Launch';

type Entity = Site;

export interface ILocationListProps extends IEntityListProps<Entity> {
	paddingX?: number | string;
	entities: Entity[];
	selected: Entity[];
	latestAssesment?: Assesment;
	simulationId?: string;
	onSelectChange?: (selected: Entity[]) => void;
	onView?: (entity: Entity) => void;
	onEdit?: (entity: Entity) => void;
	onDelete?: (entity: Entity) => void;
	onParkAreaEdit?: (entity: ParkArea) => void;
	onParkAreaDelete?: (entity: ParkArea) => void;
}

export const LocationList: FC<ILocationListProps> = ({
	entities,
	selected,
	latestAssesment,
	simulationId,
	onSelectChange,
	onView,
	onEdit,
	onDelete,
	onParkAreaEdit,
	onParkAreaDelete,
	paddingX,
	...props
}) => {
	const user = useLoggedinUser();
	const navigate = useNavigate();
	const hasWritePermission = user?.hasPermission(Permission.SITE_WRITE);
	const isTestAssesment = () => latestAssesment?.assesmentType === AssesmentType.TEST;
	const handleSelectChange = (status: boolean, entity: Entity) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	const { data: siteParkAreas = {} } = useService(
		() =>
			from(entities).pipe(
				mergeMap((site) => zip(of(site), siteService.parkArea(site.id).getAllWithAssesments(latestAssesment?.id))),
				toArray(),
				map((results) =>
					results.reduce(
						(acc, [site, parkAreas]) => ({
							...acc,
							[site.id]: parkAreas.sort((item1, item2) => item1.name.localeCompare(item2.name))
						}),
						{} as Record<string, ParkArea[]>
					)
				)
			),
		[entities, latestAssesment]
	);

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => {
				const parkAreas = siteParkAreas[entity.id] || [];
				let siteAssesmentStatus: Maybe<ParkAreaAssesmentStatus>;
				if (parkAreas.length) {
					if (parkAreas.every(({ status }) => status === ParkAreaAssesmentStatus.PASSED)) {
						siteAssesmentStatus = ParkAreaAssesmentStatus.PASSED;
					} else if (parkAreas.every(({ status }) => status === ParkAreaAssesmentStatus.NOT_PASSED)) {
						siteAssesmentStatus = ParkAreaAssesmentStatus.NOT_PASSED;
					} else if (parkAreas.every(({ status }) => status === ParkAreaAssesmentStatus.NOT_STARTED)) {
						siteAssesmentStatus = ParkAreaAssesmentStatus.NOT_STARTED;
					} else {
						siteAssesmentStatus = ParkAreaAssesmentStatus.PARTIALLY_PASSED;
					}
				}

				return (
					<EntityListItem
						key={`entity-${entity.id}-${i}`}
						sx={{ paddingRight: paddingX }}
						selected={!!selected.find((s) => s.id === entity.id)}
						notSelectable={!hasWritePermission}
						actionsType="menu"
						actions={[
							{
								text: <Tr.Portal path="view" />,
								icon: <VisibilityIcon color="inherit" />,
								onClick: () => onView?.(entity)
							},
							{
								text: <Tr.Portal path="edit" />,
								icon: <EditIcon color="inherit" />,
								onClick: () => onEdit?.(entity),
								hidden: !hasWritePermission
							},
							{
								text: <Tr.Portal path="location.see-overview" />,
								icon: <LaunchIcon color="inherit" />,
								disabled: !simulationId,
								onClick: () => (simulationId ? navigate(`./${entity.id}/infrastructure/${simulationId}`) : undefined)
							},
							{
								text: <Tr.Portal path="delete" />,
								icon: <DeleteForeverIcon color="inherit" />,
								onClick: () => onDelete?.(entity),
								hidden: !hasWritePermission
							}
						]}
						onSelectChange={(status) => handleSelectChange(status, entity)}
						expandedInitially
						disableExpandToggle
						expandedContent={
							<ParkAreaTable
								parkAreas={parkAreas || []}
								latestAssesment={latestAssesment}
								onEdit={onParkAreaEdit}
								onDelete={onParkAreaDelete}
							/>
						}
					>
						<Grid container>
							<Grid item xs={isTestAssesment() ? 3 : 4}>
								<EntityListItemCell title={<Tr.Portal path="location.location-name" />}>
									<Typography>{entity.name}</Typography>
								</EntityListItemCell>
							</Grid>
							<Grid item xs={isTestAssesment() ? 3 : 4}>
								<EntityListItemCell title={<Tr.Portal path="location.number-of-parkings" />}>
									<Typography>{NumberUtils.formatAsNumber(parkAreas.length || 0, { decimalScale: 0 })}</Typography>
								</EntityListItemCell>
							</Grid>
							<Grid item xs={isTestAssesment() ? 3 : 4}>
								<EntityListItemCell title={<Tr.Portal path="location.address" />}>
									<Typography>{entity.address}</Typography>
								</EntityListItemCell>
							</Grid>
							{isTestAssesment() ? (
								<Grid item xs={isTestAssesment() ? 3 : 4}>
									<EntityListItemCell title={<Tr.Portal path="location.assesment-status" />}>
										{siteAssesmentStatus ? (
											<Typography sx={{ fontWeight: 600, color: ASSESMENT_STATUS_COLOR_MAP[siteAssesmentStatus] }}>
												{siteAssesmentStatus === ParkAreaAssesmentStatus.NOT_STARTED && (
													<Tr.Portal path="location.not-started" />
												)}
												{siteAssesmentStatus === ParkAreaAssesmentStatus.PASSED && (
													<Tr.Portal path="location.passed" />
												)}
												{siteAssesmentStatus === ParkAreaAssesmentStatus.NOT_PASSED && (
													<Tr.Portal path="location.not-passed" />
												)}
												{siteAssesmentStatus === ParkAreaAssesmentStatus.PARTIALLY_PASSED && (
													<Tr.Portal path="location.partially-passed" />
												)}
											</Typography>
										) : (
											'-'
										)}
									</EntityListItemCell>
								</Grid>
							) : null}
						</Grid>
					</EntityListItem>
				);
			})}
		</EntityList>
	);
};
